import { useEffect, useSyncExternalStore } from "react";
import {
  anyCoachEnabled,
  coachEnabledIn,
  createCoachSettingsStore,
  useTrading,
  type CoachDoc,
  type CoachSettingsStore,
  type KeyValueStorage,
} from "@helm/core";
import { useTradingViewSlice } from "../viewmodel/account";
import "./coach.css";

/**
 * The only part of the Discipline coach in the entry chunk (audit S-12 budget): its settings
 * document (`settings/coach`, needed to know whether the coach is on at all), a cheap "is any
 * coach on?" check, and two slots: the banner row in the shells and the guard around the order
 * pad's entry controls. The reducer, rules, observer, banner and P1 card are a separate chunk
 * (`coachRuntime.ts`), loaded only once an account has the coach on. Off (the default) = nothing
 * loaded, nothing rendered, zero height.
 *
 * A chunk that fails to load shows "Coach unavailable" in the coach's own row (only when the
 * selected account has the coach on); trading, exits and the order pad are unaffected: the
 * guard always renders its children.
 */

const SYNCED_KEY = "helm:coach-settings";
const PREVIEW_KEY = "helm:coach-settings:preview";

export const coachLocalStore: KeyValueStorage = {
  get: (key) => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set: (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Quota / private mode: the coach's data simply isn't cached locally.
    }
  },
};

let preview = false;
let store: CoachSettingsStore | null = null;

/** Dev preview: coach settings stay in local storage, never the API. Call before first render. */
export function localOnlyCoachStore(): void {
  preview = true;
}

export function coachSettingsStore(): CoachSettingsStore {
  if (store === null) {
    store = createCoachSettingsStore({
      storageKey: preview ? PREVIEW_KEY : SYNCED_KEY,
      storage: coachLocalStore,
      sync: !preview,
    });
    void store.start();
  }
  return store;
}

/** Route a `settings/coach` DocChanged (session.ts) to the store. */
export function coachDocChanged(version: number): void {
  if (store === null) return;
  void store.refresh(version);
}

export function useCoachDoc(): CoachDoc {
  const s = coachSettingsStore();
  return useSyncExternalStore(s.subscribe, s.getSnapshot);
}

// ---- the lazily loaded runtime --------------------------------------------------------------

type Runtime = typeof import("./coachRuntime");
type RuntimeState =
  { status: "idle" | "loading" | "failed"; runtime: null } | { status: "ready"; runtime: Runtime };

let state: RuntimeState = { status: "idle", runtime: null };
const listeners = new Set<() => void>();

function setState(next: RuntimeState): void {
  state = next;
  for (const l of listeners) l();
}

/** Load the coach runtime chunk (idempotent; after a failure only `retry` loads again). */
export function loadCoachRuntime(retry = false): void {
  if (state.status === "loading" || state.status === "ready") return;
  if (state.status === "failed" && !retry) return;
  setState({ status: "loading", runtime: null });
  import("./coachRuntime").then(
    (runtime) => {
      setState({ status: "ready", runtime });
    },
    () => {
      setState({ status: "failed", runtime: null });
    },
  );
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

export function useCoachRuntime(): RuntimeState {
  return useSyncExternalStore(subscribe, () => state);
}

const sameId = (a: string | null, b: string | null): boolean => a === b;

/**
 * The coach's row in the shell's banner stack. Nothing (zero height) unless an account has the
 * coach on; then the runtime is loaded and its banner takes over. While it loads, the selected
 * account's row is reserved at its fixed height, so the chart and DOM do not shift.
 */
export function CoachSlot(): React.JSX.Element | null {
  const doc = useCoachDoc();
  const any = anyCoachEnabled(doc);
  const accountId = useTradingViewSlice((v) => v.activeId, sameId);
  const loginId = useTrading((t) => (accountId === null ? "" : (t.accounts.get(accountId)?.loginId ?? "")));
  const rt = useCoachRuntime();
  useEffect(() => {
    if (any) loadCoachRuntime();
  }, [any]);
  if (!any) return null;
  if (rt.status === "ready") return <rt.runtime.CoachBanner />;
  if (accountId === null || !coachEnabledIn(doc, loginId, accountId)) return null;
  return (
    <div
      className="coach-line coach-line-unavailable"
      role="status"
      aria-live="polite"
      data-testid="coach-line"
    >
      {rt.status === "failed" && (
        <>
          <span className="coach-text">Coach unavailable. Trading is not affected.</span>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => {
              loadCoachRuntime(true);
            }}
          >
            Retry
          </button>
        </>
      )}
    </div>
  );
}

/**
 * Wraps an order pad's ENTRY controls. The children always render; once the runtime is loaded,
 * its P1 card may cover them (never the exits, which sit outside this wrapper).
 */
export function CoachEntryGuard({
  accountId,
  children,
}: {
  accountId: string | null;
  children: React.ReactNode;
}): React.JSX.Element {
  const rt = useCoachRuntime();
  return (
    <div className="coach-entry-guard">
      {children}
      {rt.status === "ready" && <rt.runtime.CoachEntryCard accountId={accountId} />}
    </div>
  );
}
